'use client';
import { ElementType, ReactNode } from 'react';
import { IconArrowBack } from '../../Icons';
import { Box, Typography } from '../../mui';
import React from 'react';

const ConditionalBackLink = ({
  children,
  backHref,
  component,
}: {
  children: ReactNode;
  backHref?: string;
  component?: ElementType;
}) => {
  if (!backHref) {
    return <>{children}</>;
  }

  return (
    <Box
      href={backHref}
      to={backHref}
      component={component ?? 'a'}
      sx={(theme) => ({
        display: { xs: 'flex', md: 'none' },
        alignItems: 'center',
        gap: 1,
        color: 'primary.dark',
        ...theme.applyStyles('dark', {
          color: 'common.white',
        }),
        textDecoration: 'none',
        '&:hover, &:focus': {
          textDecoration: 'underline',
        },
      })}
    >
      <IconArrowBack />
      {children}
    </Box>
  );
};

type BorderHeadingProps = {
  overline?: string;
  title: string | ReactNode;
  publicationDetails?: string | ReactNode;
  subtitle?: ReactNode | string;
  small?: boolean;
  backHref?: string;
  linkComponent?: ElementType;
  component?: ElementType;
  maskTitle?: boolean;
};

const BorderHeading = ({
  overline,
  title,
  publicationDetails,
  subtitle,
  small,
  backHref,
  linkComponent = 'a',
  component = 'h1',
  maskTitle = false,
}: BorderHeadingProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'baseline',
        pb: 2,
      }}
    >
      <Box
        sx={{
          mr: 2,
          display: 'inline-block',
          maxWidth: '100%',
          position: 'relative',
          pl: 2.5,
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: '8px',
            borderRadius: '4px',
            bgcolor: 'accent.main',
          },
        }}
      >
        {overline && (
          <ConditionalBackLink backHref={backHref} component={linkComponent}>
            <Typography
              variant='overline'
              color={backHref ? 'inherit' : 'text.secondary'}
              sx={{ display: 'inline-block' }}
            >
              {overline}
            </Typography>
          </ConditionalBackLink>
        )}
        <Typography
          variant={small ? 'h3' : 'h1'}
          component={component}
          sx={{
            color: 'primary.main',
            display: 'block',
          }}
          data-clarity-mask={maskTitle ? 'True' : undefined}
        >
          {title}
        </Typography>
        {publicationDetails && (
          <Typography
            variant='caption'
            color='textSecondary'
            sx={{ display: 'inline-block' }}
          >
            {publicationDetails}
          </Typography>
        )}
      </Box>
      {(React.isValidElement(subtitle) || typeof subtitle === 'string') && (
        <Typography
          variant='preamble'
          component={typeof subtitle === 'string' ? 'p' : 'div'}
          color='textSecondary'
          sx={{ mt: 1, maxWidth: '70ch' }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export default BorderHeading;
